import { measure, QUALITY, toGray, type FrameMetrics } from "../camera/frame-metrics"

// Every photo the consumer gives us goes through here before upload:
//   - accepts any image the browser can decode (some Android pickers report
//     an empty MIME type — we try to decode instead of rejecting);
//   - re-encodes to JPEG ≤ 2048px: bakes the EXIF orientation in and strips
//     EXIF (no GPS from a gallery photo ever leaves the phone);
//   - measures light + sharpness on a 160px copy for the instant on-device
//     check, before the network is involved;
//   - a camera still (4:3) is cut to the live picture's shape, the middle
//     of it: the photo is exactly what the person framed.

const MAX_EDGE = 2048
const HARD_MAX_BYTES = 40 * 1024 * 1024
const JPEG_QUALITY = 0.88

export interface PreparedPhoto {
  file: File
  metrics: FrameMetrics
  width: number
  height: number
}

export type PrepareFailure = "not-image" | "too-large" | "unreadable"

export type PrepareResult = { ok: true; photo: PreparedPhoto } | { ok: false; reason: PrepareFailure }

type Drawable = ImageBitmap | HTMLImageElement | HTMLCanvasElement

function sizeOf(src: Drawable): { width: number; height: number } {
  if (src instanceof HTMLImageElement) return { width: src.naturalWidth, height: src.naturalHeight }
  return { width: src.width, height: src.height }
}

async function decode(file: Blob): Promise<Drawable> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" })
    } catch {
      // fall through to <img> (older Safari, some formats)
    }
  }
  const url = URL.createObjectURL(file)
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error("image-load-failed"))
      img.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

// The part of the picture to keep.
export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

// The middle of a width × height picture in the shape of `shape` (held the
// same way), or all of it.
export function cropToShape(width: number, height: number, shape?: { w: number; h: number }): Rect {
  const all = { x: 0, y: 0, w: width, h: height }
  if (!shape || !(shape.w > 0 && shape.h > 0) || !(width > 0 && height > 0)) return all
  const want = shape.w / shape.h
  const have = width / height
  if (Math.abs(want - have) < 0.01) return all
  if (have > want) {
    const w = Math.round(height * want)
    return { x: Math.round((width - w) / 2), y: 0, w, h: height }
  }
  const h = Math.round(width / want)
  return { x: 0, y: Math.round((height - h) / 2), w: width, h }
}

// Light and sharpness of the picture (or the part of it that's kept).
export function metricsOf(src: Drawable, rect?: Rect): FrameMetrics {
  const { width, height } = sizeOf(src)
  const r = rect ?? { x: 0, y: 0, w: width, h: height }
  const w = QUALITY.METRICS_WIDTH
  const h = Math.max(1, Math.round((r.h / Math.max(1, r.w)) * w))
  const canvas = document.createElement("canvas")
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext("2d", { willReadFrequently: true })
  if (!ctx) return { luma: 128, sharpness: QUALITY.BLUR_FLOOR * 10 }
  ctx.drawImage(src, r.x, r.y, r.w, r.h, 0, 0, w, h)
  const { data } = ctx.getImageData(0, 0, w, h)
  return measure(toGray(data, w, h))
}

function encode(src: Drawable, rect: Rect, width: number, height: number): Promise<Blob> {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext("2d")
  if (!ctx) return Promise.reject(new Error("no-2d-context"))
  ctx.drawImage(src, rect.x, rect.y, rect.w, rect.h, 0, 0, width, height)
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob-null"))), "image/jpeg", JPEG_QUALITY),
  )
}

function baseName(name: string): string {
  const base = name.replace(/\.[^.]+$/, "").replace(/[^\w.-]+/g, "-").slice(0, 60)
  return base || "photo"
}

// `shape`: cut to the middle of the picture in this shape (a camera still
// cut to what the live picture showed).
export async function preparePhoto(input: Blob, name: string, { shape }: { shape?: { w: number; h: number } } = {}): Promise<PrepareResult> {
  if (input.type && !input.type.startsWith("image/")) return { ok: false, reason: "not-image" }
  if (input.size > HARD_MAX_BYTES) return { ok: false, reason: "too-large" }

  let src: Drawable
  try {
    src = await decode(input)
  } catch {
    return { ok: false, reason: input.type ? "unreadable" : "not-image" }
  }

  try {
    const { width, height } = sizeOf(src)
    if (!width || !height) return { ok: false, reason: "unreadable" }
    const keep = cropToShape(width, height, shape)
    const scale = Math.min(1, MAX_EDGE / Math.max(keep.w, keep.h))
    const w = Math.round(keep.w * scale)
    const h = Math.round(keep.h * scale)
    const blob = await encode(src, keep, w, h)
    return {
      ok: true,
      photo: {
        file: new File([blob], `${baseName(name)}.jpg`, { type: "image/jpeg", lastModified: Date.now() }),
        metrics: metricsOf(src, keep),
        width: w,
        height: h,
      },
    }
  } catch {
    return { ok: false, reason: "unreadable" }
  } finally {
    if ("close" in src && typeof src.close === "function") src.close()
  }
}
